import {
  useGetBuildingUseListQuery,
  useGetHeatInstallationListQuery,
  useGetMatrikelMetadataByIdQuery,
  usePostPortfolioUpdateNowMutation,
  useUpdateBenchmarkOverwriteMutation,
} from '@/api/energy-analysis/portfolioAnalysisApiSlice';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
const EditMatrikelDataDialog = ({
  open,
  onClose,
  address = '',
  matrikelKey,
}) => {
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const { data: matrikelMetadata } = useGetMatrikelMetadataByIdQuery(
    {
      recalc_account_id: account?.AccountId,
      matrikel_key: matrikelKey,
    },
    { skip: !account?.AccountId || !matrikelKey },
  );

  const { data: heatInstallationList } = useGetHeatInstallationListQuery();
  const { data: buildingUseList } = useGetBuildingUseListQuery();

  const initialState = useMemo(() => {
    if (matrikelMetadata?.OverwriteFlag) {
      return {
        AverageBuildingYear:
          matrikelMetadata?.OverwriteAverageBuildingYear ??
          matrikelMetadata?.AverageBuildingYear,
        SumBuildingArea:
          matrikelMetadata?.OverwriteSumBuildingArea ??
          matrikelMetadata?.SumBuildingArea,
        ExtractedBuildingUse:
          matrikelMetadata?.OverwriteExtractedBuildingUse ??
          matrikelMetadata?.ExtractedBuildingUse,
        ExtractedHeatInstallation:
          matrikelMetadata?.OverwriteExtractedHeatInstallation ??
          matrikelMetadata?.ExtractedHeatInstallation,
        VerificationFlag: matrikelMetadata?.VerificationFlag ?? false,
      };
    } else {
      return {
        AverageBuildingYear: matrikelMetadata?.AverageBuildingYear,
        SumBuildingArea: matrikelMetadata?.SumBuildingArea,
        ExtractedBuildingUse: matrikelMetadata?.ExtractedBuildingUse,
        ExtractedHeatInstallation: matrikelMetadata?.ExtractedHeatInstallation,
        VerificationFlag: matrikelMetadata?.VerificationFlag ?? false,
      };
    }
  }, [matrikelMetadata]);

  const isOverwritten = useCallback(
    (field) => {
      if (!matrikelMetadata?.OverwriteFlag) return false;
      const overwriteField = `Overwrite${field}`;
      return matrikelMetadata?.[overwriteField] !== null;
    },
    [matrikelMetadata],
  );

  const [state, setState] = useState(initialState);

  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const isEdited = useMemo(() => {
    // If the state is the same as the initial state, return false
    if (JSON.stringify(state) === JSON.stringify(initialState)) {
      return false;
    }
    // If the state is different from the initial state, return true
    return true;
  }, [state, initialState]);

  const [updateBenchmarkOverwrite] = useUpdateBenchmarkOverwriteMutation();
  const [updatePortfolioAnalysis] = usePostPortfolioUpdateNowMutation();
  const handleSaveChanges = async () => {
    const changes = {};
    Object.keys(state).forEach((key) => {
      if (state[key] !== initialState[key]) {
        changes[key] = state[key];
      } else {
        changes[key] = null;
      }
      changes.OverwriteFlag = true;
      changes.VerificationFlag = state.VerificationFlag;
    });

    let response = await updateBenchmarkOverwrite({
      account_id: account?.AccountId,
      matrikel_key: matrikelKey,
      body: changes,
    });

    if (response?.error?.status) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdateringen',
          severity: 'error',
        }),
      );
      return;
    } else {
      dispatch(
        showSnackbar({
          message:
            'Ændringerne er gemt. Bemærk at der kan gå op til 1 time før ændringen tager effekt.',
          severity: 'success',
        }),
      );
      updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    let response = await updateBenchmarkOverwrite({
      matrikel_key: matrikelKey,
      account_id: account?.AccountId,
      body: { ...state, OverwriteFlag: false, VerificationFlag: false },
    });

    if (response?.error?.status) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdateringen',
          severity: 'error',
        }),
      );
      return;
    } else {
      dispatch(
        showSnackbar({
          message:
            'Ændringerne er gemt. Bemærk at der kan gå op til 1 time før ændringen tager effekt.',
          severity: 'success',
        }),
      );
      updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Indstillinger for {address}</DialogTitle>
      {state ? (
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label={`Bygningsår ${
                isOverwritten('AverageBuildingYear') ? '(Redigeret)' : ''
              }`}
              value={state?.AverageBuildingYear?.toFixed(0) ?? ''}
              onChange={(e) => handleChange(e)}
              fullWidth
              variant="filled"
              name="AverageBuildingYear"
            />
            <TextField
              label={`Bygningsareal ${
                isOverwritten('SumBuildingArea') ? '(Redigeret)' : ''
              }`}
              value={state?.SumBuildingArea ?? ''}
              onChange={(e) => handleChange(e)}
              fullWidth
              variant="filled"
              name="SumBuildingArea"
            />
            <ListItemText
              primary="Bygningsanvendelse"
              secondary="Vælg den kategori din bygning primært bliver brugt til."
            />
            <TextField
              select
              label={`Bygningsanvendelse ${
                isOverwritten('ExtractedBuildingUse') ? '(Redigeret)' : ''
              }`}
              value={state?.ExtractedBuildingUse ?? ''}
              onChange={(e) => handleChange(e)}
              fullWidth
              variant="filled"
              name="ExtractedBuildingUse"
            >
              <MenuItem key={0} value={''}>
                ...
              </MenuItem>
              {buildingUseList?.map((buildingUse, index) => (
                <MenuItem key={index + 1} value={buildingUse}>
                  {buildingUse}
                </MenuItem>
              ))}
            </TextField>
            <ListItemText
              primary="Varmeanlæg"
              secondary="Vælg den varmeløsning din bygning primært bruger."
            />
            <TextField
              select
              label={`Varmeanlæg ${
                isOverwritten('ExtractedHeatInstallation') ? '(Redigeret)' : ''
              }`}
              value={state.ExtractedHeatInstallation ?? ''}
              onChange={(e) => handleChange(e)}
              fullWidth
              variant="filled"
              name="ExtractedHeatInstallation"
            >
              <MenuItem key={0} value={''}>
                ...
              </MenuItem>
              {heatInstallationList?.map((installation, index) => (
                <MenuItem key={index + 1} value={installation}>
                  {installation}
                </MenuItem>
              ))}
            </TextField>
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={
                    state.VerificationFlag !== undefined
                      ? state.VerificationFlag
                      : false
                  }
                  onChange={(e) =>
                    handleChange({
                      target: { name: e.target.name, value: e.target.checked },
                    })
                  }
                  name="VerificationFlag"
                />
              }
              label="Klar til benchmarking"
            /> */}
          </Stack>
        </DialogContent>
      ) : (
        <CircularProgress />
      )}
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={handleDelete}>
          Gendan fra BBR
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveChanges}
          disabled={!isEdited}
        >
          Gem ændringer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMatrikelDataDialog;
